import React, { useState } from 'react';
import { View, StyleSheet, Alert } from 'react-native';
import { Text, Button, Card, useTheme } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AISkillAssessment } from '../components/ai';
import type { AssessmentResults } from '../components/ai/types';
import { logger } from '../utils/logger';

export function AIAssessmentScreen() {
  const theme = useTheme();
  const [showAssessment, setShowAssessment] = useState(false);
  const [assessmentResults, setAssessmentResults] =
    useState<AssessmentResults | null>(null);

  const handleAssessmentComplete = (results: AssessmentResults) => {
    setAssessmentResults(results);
    setShowAssessment(false);

    Alert.alert(
      'Assessment Complete!',
      `Skill Level: ${results.skillLevel}\nConfidence: ${Math.round(results.overallConfidence * 100)}%\nDuration: ${Math.round(results.duration)} minutes`,
      [{ text: 'OK' }]
    );
  };

  const handleProgress = (step: number, total: number) => {
    logger.log(`Assessment progress: ${step}/${total}`);
  };

  if (showAssessment) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <AISkillAssessment
          onComplete={handleAssessmentComplete}
          onProgress={handleProgress}
          allowSkip={true}
          showProgress={true}
        />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <View style={styles.content}>
        <Card style={styles.introCard} mode='outlined'>
          <Card.Content>
            <Text variant='headlineMedium' style={styles.title}>
              AI Skill Assessment
            </Text>
            <Text
              variant='bodyLarge'
              style={[
                styles.description,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Take our interactive coffee skill assessment to get personalized
              learning recommendations tailored to your experience level and
              preferences.
            </Text>

            <View style={styles.features}>
              <Text variant='bodyMedium' style={styles.featureTitle}>
                What you'll get:
              </Text>
              <Text variant='bodySmall' style={styles.feature}>
                • Personalized coffee learning journey
              </Text>
              <Text variant='bodySmall' style={styles.feature}>
                • Skill-based content recommendations
              </Text>
              <Text variant='bodySmall' style={styles.feature}>
                • Equipment-specific brewing guides
              </Text>
              <Text variant='bodySmall' style={styles.feature}>
                • Adaptive difficulty progression
              </Text>
            </View>

            <Button
              mode='contained'
              onPress={() => setShowAssessment(true)}
              style={styles.startButton}
            >
              Start Assessment
            </Button>
          </Card.Content>
        </Card>

        {assessmentResults && (
          <Card style={styles.resultsCard} mode='outlined'>
            <Card.Content>
              <Text variant='titleLarge' style={styles.resultsTitle}>
                Last Assessment Results
              </Text>
              <View style={styles.resultItem}>
                <Text variant='bodyMedium' style={styles.resultLabel}>
                  Skill Level:
                </Text>
                <Text variant='bodyMedium' style={styles.resultValue}>
                  {assessmentResults.skillLevel}
                </Text>
              </View>
              <View style={styles.resultItem}>
                <Text variant='bodyMedium' style={styles.resultLabel}>
                  Confidence:
                </Text>
                <Text variant='bodyMedium' style={styles.resultValue}>
                  {Math.round(assessmentResults.overallConfidence * 100)}%
                </Text>
              </View>
              <View style={styles.resultItem}>
                <Text variant='bodyMedium' style={styles.resultLabel}>
                  Duration:
                </Text>
                <Text variant='bodyMedium' style={styles.resultValue}>
                  {Math.round(assessmentResults.duration)} minutes
                </Text>
              </View>
              <View style={styles.resultItem}>
                <Text variant='bodyMedium' style={styles.resultLabel}>
                  Learning Goals:
                </Text>
                <Text variant='bodyMedium' style={styles.resultValue}>
                  {assessmentResults.learningGoals.join(', ') ||
                    'None specified'}
                </Text>
              </View>

              <Button
                mode='outlined'
                onPress={() => setShowAssessment(true)}
                style={styles.retakeButton}
              >
                Retake Assessment
              </Button>
            </Card.Content>
          </Card>
        )}
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  introCard: {
    marginBottom: 16,
  },
  title: {
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  description: {
    lineHeight: 22,
    marginBottom: 16,
    textAlign: 'center',
  },
  features: {
    marginBottom: 20,
  },
  featureTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  feature: {
    marginBottom: 4,
    paddingLeft: 8,
  },
  startButton: {
    marginTop: 8,
  },
  resultsCard: {
    marginTop: 8,
  },
  resultsTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  resultItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  resultLabel: {
    fontWeight: '500',
  },
  resultValue: {
    flex: 1,
    textAlign: 'right',
  },
  retakeButton: {
    marginTop: 12,
  },
});
